import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Form, FormField } from '../services/formService';

interface FormRendererProps {
  form: Form;
  onSubmit: (formData: Record<string, any>) => void;
  isSubmitting?: boolean;
  currentStep?: number;
  totalSteps?: number;
  submitButtonText?: string;
  onPrevStep?: () => void;
  initialValues?: Record<string, any>;
}

export interface FormRendererRef {
  getCurrentFormData: () => Record<string, any>;
}

/**
 * Renders a form created with the form designer
 * Handles form submission and validation
 */
const FormRenderer = forwardRef<FormRendererRef, FormRendererProps>(({ 
  form, 
  onSubmit,
  isSubmitting = false,
  currentStep,
  totalSteps,
  submitButtonText,
  onPrevStep,
  initialValues = {}
}, ref) => {
  // Get the form prefix from the form name
  const formPrefix = form.name.toLowerCase().replace(/[^\w]/g, '') || 'form';
  
  // State for form values - initialize with provided initial values
  const [formValues, setFormValues] = useState<Record<string, any>>(initialValues);
  
  // State for validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Expose form data to parent via ref
  useImperativeHandle(ref, () => ({
    getCurrentFormData: () => formValues
  }), [formValues]);
  
  // Update form values when initialValues change (e.g., when user navigates back)
  useEffect(() => {
    if (initialValues && Object.keys(initialValues).length > 0) {
      setFormValues(initialValues);
    }
  }, [initialValues]);
  
  // Handle form field change
  const handleChange = (field: FormField, value: any) => {
    // Use field.name for variable storage (for prompt substitution)
    // but use field.id for UI state tracking (to prevent synchronization issues)
    // Also check for variableName property used by SimpleFormDesigner
    const fieldWithVariableName = field as any;
    const variableName = field.name || fieldWithVariableName.variableName || field.id; // Check all possible variable name properties
    const fullVariableName = `${formPrefix}.${variableName}`;
    
    setFormValues(prev => ({
      ...prev,
      [fullVariableName]: value
    }));
    
    // Clear error for this field if it exists
    if (errors[fullVariableName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fullVariableName];
        return newErrors;
      });
    }
  };
  
  // Get the value for a field (using field.name or variableName for variable lookup)
  const getFieldValue = (field: FormField): any => {
    // Also check for variableName property used by SimpleFormDesigner
    const fieldWithVariableName = field as any;
    const variableName = field.name || fieldWithVariableName.variableName || field.id; // Check all possible variable name properties
    const fullVariableName = `${formPrefix}.${variableName}`;
    // First try the prefixed name with field.name/variableName, then fall back to other options for backward compatibility
    return formValues[fullVariableName] ?? 
           formValues[`${formPrefix}.${field.id}`] ?? 
           formValues[`${formPrefix}.${field.name}`] ?? 
           formValues[`${formPrefix}.${fieldWithVariableName.variableName}`] ?? 
           formValues[field.name] ?? 
           formValues[fieldWithVariableName.variableName] ?? 
           formValues[field.id] ?? 
           '';
  };
  
  // Validate the form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    form.fields.forEach(field => {
      // Skip non-input fields like headers and paragraphs
      if (['header', 'paragraph'].includes(field.type)) {
        return;
      }
      
      // Use the same logic as handleChange to get the correct variable name
      const fieldWithVariableName = field as any;
      const variableName = field.name || fieldWithVariableName.variableName || field.id;
      const fullVariableName = `${formPrefix}.${variableName}`;
      const fieldValue = getFieldValue(field);
      
      // Check required fields
      if (field.required && 
          (fieldValue === undefined || 
           fieldValue === null || 
           fieldValue === '')) {
        newErrors[fullVariableName] = `${field.label} is required`;
      }
      
      // Additional validation could be added here
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(formValues);
    }
  };
  
  // Helper function to preserve line breaks in text
  const renderTextWithLineBreaks = (text: string) => {
    return text.split('\n').map((line, index, array) => (
      <React.Fragment key={index}>
        {line}
        {index < array.length - 1 && <br />}
      </React.Fragment>
    ));
  };
  
  // Render a field based on its type
  const renderField = (field: FormField) => {
    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            id={field.id}
            value={getFieldValue(field) || ''}
            onChange={(e) => handleChange(field, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white placeholder-gray-500"
            placeholder={field.placeholder || ''}
            disabled={isSubmitting}
          />
        );
        
      case 'textarea':
        return (
          <textarea
            id={field.id}
            value={getFieldValue(field) || ''}
            onChange={(e) => handleChange(field, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical min-h-[100px] text-gray-900 bg-white placeholder-gray-500"
            placeholder={field.placeholder || ''}
            rows={4}
            disabled={isSubmitting}
          />
        );
        
      case 'number':
        return (
          <input
            type="number"
            id={field.id}
            value={getFieldValue(field) || ''}
            onChange={(e) => handleChange(field, e.target.value ? Number(e.target.value) : '')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white placeholder-gray-500"
            placeholder={field.placeholder || ''}
            disabled={isSubmitting}
          />
        );
        
      case 'select':
        return (
          <select
            id={field.id}
            value={getFieldValue(field) || ''}
            onChange={(e) => handleChange(field, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white"
            disabled={isSubmitting}
          >
            <option value="" className="text-gray-500">Select an option...</option>
            {field.options?.map((option) => {
              // Handle both string and object options
              const value = typeof option === 'string' ? option : (option as any).value;
              const label = typeof option === 'string' ? option : (option as any).label;
              return (
                <option key={value} value={value} className="text-gray-900">
                  {label}
              </option>
              );
            })}
          </select>
        );
        
      case 'checkbox':
        return (
          <div className="flex items-center">
            <input
              type="checkbox"
              id={field.id}
              checked={!!getFieldValue(field)}
              onChange={(e) => handleChange(field, e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              disabled={isSubmitting}
            />
            <label htmlFor={field.id} className="ml-2 block text-gray-700">
              {field.label}
            </label>
          </div>
        );
        
      case 'radio':
        return (
          <div className="space-y-2">
            {field.options?.map((option) => {
              // Handle both string and object options
              const value = typeof option === 'string' ? option : (option as any).value;
              const label = typeof option === 'string' ? option : (option as any).label;
              return (
                <div key={value} className="flex items-center">
                <input
                  type="radio"
                    id={`${field.id}_${value}`}
                  name={field.id}
                    value={value}
                    checked={getFieldValue(field) === value}
                    onChange={() => handleChange(field, value)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  disabled={isSubmitting}
                />
                  <label htmlFor={`${field.id}_${value}`} className="ml-2 block text-gray-700">
                    {label}
                </label>
              </div>
              );
            })}
          </div>
        );
        
      case 'date':
        return (
          <input
            type="date"
            id={field.id}
            value={getFieldValue(field) || ''}
            onChange={(e) => handleChange(field, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white placeholder-gray-500"
            disabled={isSubmitting}
          />
        );
        
      case 'document': {
        const file = getFieldValue(field);
        return (
          <div>
            <div 
              className="w-full px-3 py-2 border border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center h-32 bg-gray-50 hover:bg-gray-100 transition-colors relative"
            >
              {!file ? (
                <>
                  <span className="material-icons text-gray-400" style={{ fontSize: '36px' }}>
                    cloud_upload
                  </span>
                  <p className="text-gray-500 mt-2">
                    Drag & drop a file or click to upload
                  </p>
                  <input 
                    type="file"
                    id={field.id} 
                    className="absolute w-full h-full opacity-0 cursor-pointer"
                    onChange={(e) => {
                      if (e.target.files && e.target.files.length > 0) {
                        handleChange(field, e.target.files[0]);
                      }
                    }}
                    disabled={isSubmitting}
                  />
                </>
              ) : (
                <div className="text-center">
                  <p className="font-medium text-gray-700">{file.name}</p>
                  <p className="text-sm text-gray-500">
                    ({(file.size / 1024).toFixed(2)} KB)
                  </p>
                  <button
                    type="button"
                    onClick={() => handleChange(field, null)}
                    className="mt-2 text-sm text-red-600 hover:text-red-800"
                    disabled={isSubmitting}
                  >
                    Remove
                  </button>
              </div>
              )}
            </div>
          </div>
        );
      }
        
      default:
        return <div>Unsupported field type: {field.type}</div>;
    }
  };
  
  return (
    <div className="bg-white">
      {/* Progress Indicator */}
      {currentStep && totalSteps && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Step {currentStep} of {totalSteps}
            </span>
            <span className="text-sm text-gray-500">
              {Math.round((currentStep / totalSteps) * 100)}% Complete
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="h-2 rounded-full transition-all duration-300 ease-out"
              style={{ 
                width: `${(currentStep / totalSteps) * 100}%`,
                backgroundColor: '#1d3a8b'
              }}
            ></div>
          </div>
        </div>
      )}
      
      <div className="border-b border-gray-200 pb-4 mb-6">
        <h3 className="text-xl font-medium leading-6 text-gray-900">
          {form.name}
        </h3>
        {form.description && (
          <div className="mt-1 text-sm text-gray-500 leading-relaxed">
            {renderTextWithLineBreaks(form.description)}
          </div>
        )}
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {form.fields.map((field) => (
          <div key={field.id} className="space-y-1">
            {/* Don't show label for checkbox (already showing in the component) and headers/paragraphs */}
            {!['checkbox', 'header', 'paragraph'].includes(field.type) && (
              <label htmlFor={field.id} className="block text-sm font-medium text-gray-700">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
            )}
            
            {(field as any).helpText && !['header', 'paragraph'].includes(field.type) && (
              <div className="text-xs text-gray-500 mb-1 leading-relaxed">
                {renderTextWithLineBreaks((field as any).helpText)}
              </div>
            )}
            
            {renderField(field)}
            
            {errors[`${formPrefix}.${(field as any).variableName || field.name || field.id}`] && (
              <p className="mt-1 text-sm text-red-600">{errors[`${formPrefix}.${(field as any).variableName || field.name || field.id}`]}</p>
            )}
          </div>
        ))}
        
        <div className="pt-4 border-t border-gray-200 mt-6">
          <div className="flex justify-between items-center pt-4">
            {/* Back button - only show if not first step and onPrevStep is provided */}
            {currentStep && currentStep > 1 && onPrevStep ? (
              <button
                type="button"
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                onClick={onPrevStep}
                disabled={isSubmitting}
              >
                <span className="material-icons mr-2" style={{ fontSize: '18px' }}>arrow_back</span>
                Back
              </button>
            ) : (
              <div></div>
            )}
            
            {/* Submit button */}
            <button
              type="submit"
              className={`inline-flex items-center justify-center py-2 px-4 border border-transparent text-sm font-semibold rounded-lg text-white transition-all duration-200 ease-in-out ${
                isSubmitting ? 'bg-gray-400' : 'bg-[#1d3a8b] hover:bg-[#152d6f]'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1d3a8b] disabled:opacity-60 disabled:cursor-not-allowed`}
              disabled={isSubmitting}
              style={{ gap: '8px' }}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting...
                </>
              ) : (
                <>
                  {submitButtonText || 'Submit'}
                  <span className="material-icons ml-2" style={{ fontSize: '18px' }}>arrow_forward</span>
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
});

export default FormRenderer; 